import React, {useEffect, useState, useCallback} from "react";
import {Table, Typography, Spin, message, Select, Row, Col, Modal, Descriptions} from "antd";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import {useSelector} from "react-redux";
import statisticsService from "../services/StatisticsService";
import theme from "../theme";
import {QUERY_STATE_ROLE, STATE_ROLE, useUserRole} from "../utils/userSessionUtils";

const {Option} = Select;
const {Title} = Typography;

const StatisticsPage = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [entityOptions, setEntityOptions] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [sectionOptions, setSectionOptions] = useState([]);
    const [selectedSections, setSelectedSections] = useState([]);

    const [selectedStat, setSelectedStat] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const isMobile = useMediaQuery({ maxWidth: 800 });
    const auth = useSelector((state) => state.auth || {});
    const user = auth.user || {};
    const role = useUserRole();
    const { entityId, sections = [], route = [] } = user;

    // Lógica para cargar estadísticas
    const fetchData = useCallback(
        async ({
                   entityIdFilter = entityId,
                   sectionsFilter = null,
                   page = 1,
                   size = 10
               }) => {
            const finalSectionsFilter = sectionsFilter || (role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? route : sections);

            if (!entityIdFilter) return;
            setLoading(true);

            try {
                const response = await statisticsService.getStatistics(
                    entityIdFilter,
                    null,
                    finalSectionsFilter?.length ? finalSectionsFilter.join(",") : null,
                    page,
                    size
                );
                const { data, meta } = response.data;

                setStats(data);
                setCurrentPage(page);
                setPageSize(size);
                setTotalItems(meta.totalItems);
            } catch (error) {
                console.error("Error fetching statistics:", error);
                message.error("Error al obtener las estadísticas.");
            } finally {
                setLoading(false);
            }
        },
        [sections, route, entityId, role]
    );

    // Lógica para cargar secciones desde Goals
    const fetchSections = useCallback(async (entityId) => {
        if (!entityId) return;

        setLoading(true);
        try {
            if (role === STATE_ROLE || role === QUERY_STATE_ROLE) {
                // Para roles estatales, obtener secciones desde Goals
                const response = await statisticsService.getStatistics(entityId, null, null, 1, 10000);
                const uniqueSections = [...new Set(response.data.data.map(stat => stat.sectionId))];
                setSectionOptions(uniqueSections.sort((a, b) => a - b));
            } else {
                // Para roles no estatales, usar las secciones de su ruta
                setSectionOptions(route.sort((a, b) => a - b));
            }
        } catch (error) {
            console.error("Error fetching sections:", error);
            message.error("Error al obtener las secciones.");
        } finally {
            setLoading(false);
        }
    }, [role, route]);

    // Lógica para cargar entidades
    const fetchEntities = useCallback(() => {
        if (role === STATE_ROLE || role === QUERY_STATE_ROLE) {
            const options = [entityId].map((entity) => ({
                id: entity,
                name: `Entidad ${entity}`,
            }));
            setEntityOptions(options);

            const isEntityValid = options.some((option) => option.id === entityId);
            if (isEntityValid) {
                setSelectedEntity(entityId);
            } else {
                console.warn("El entityId del usuario no se encuentra disponible en las opciones.");
            }
        }
    }, [role, entityId]);

    // useEffect separado para cargar entidades al iniciar
    useEffect(() => {
        if (role === STATE_ROLE || role === QUERY_STATE_ROLE) {
            fetchEntities();
        }
    }, [role, fetchEntities]);

    // useEffect para cargar secciones cuando cambia la entidad seleccionada (sin distrito)
    useEffect(() => {
        if (selectedEntity) {
            fetchSections(selectedEntity).catch(console.error);
        }
    }, [selectedEntity, fetchSections]);

    // useEffect para cargar estadísticas cada vez que cambien las selecciones
    useEffect(() => {
        const defaultSections = role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? route : sections;
        fetchData({
            sectionsFilter: selectedSections.length ? selectedSections : defaultSections,
            entityIdFilter: selectedEntity || entityId,
            page: currentPage,
            size: pageSize,
        }).catch(console.error);
    }, [fetchData, selectedSections, selectedEntity, currentPage, pageSize, sections, route, entityId, role]);

    // Función para manejar el cambio de entidad
    const handleEntityChange = (entityId) => {
        setSelectedEntity(entityId);
        setSelectedSections([]); // Limpiar secciones seleccionadas
    };

    // Función para manejar el cambio de secciones
    const handleSectionChange = (values) => {
        setSelectedSections(values);
        setCurrentPage(1); // Reiniciar la paginación
    };

    // Función para manejar cambios en la tabla
    const handleTableChange = (newPagination) => {
        const defaultSections = role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? route : sections;
        fetchData({
            page: newPagination.current,
            size: newPagination.pageSize,
            sectionsFilter: selectedSections.length ? selectedSections : defaultSections,
            entityIdFilter: role !== STATE_ROLE && role !== QUERY_STATE_ROLE ? entityId : selectedEntity,
        }).catch(console.error);
    };

    const columns = [
        { title: "Entidad", dataIndex: "entityId", key: "entityId" },
        { title: "Sección", dataIndex: "sectionId", key: "sectionId" },
        { title: "Meta mínima", dataIndex: "goal", key: "goal" },
        { title: "Asistencia", dataIndex: "rollCallCount", key: "rollCallCount" },
        { title: "Avance", dataIndex: "totalCount", key: "totalCount" },
        {
            title: "Avance (%)",
            dataIndex: "progressPercentage",
            key: "progressPercentage",
            render: (value) => `${value.toFixed(2)}%`,
        },
    ];


    return (
        <>
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <div style={{textAlign: "center", marginBottom: "20px"}}>
                    <Title level={2}>📊 Estadísticas de Convencidos</Title>
                </div>

                <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                    {(role === STATE_ROLE || role === QUERY_STATE_ROLE) && (
                        <Col xs={24}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                                Entidad:
                            </label>
                            <Select
                                value={selectedEntity}
                                placeholder="Selecciona una entidad"
                                style={{width: "100%"}}
                                onChange={handleEntityChange}
                                disabled={loading}
                            >
                                {entityOptions.map((entity) => (
                                    <Option key={entity.id} value={entity.id}>
                                        {entity.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    )}
                    <Col xs={24}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            Secciones:
                        </label>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Selecciona secciones"
                            value={selectedSections}
                            onChange={handleSectionChange}
                            style={{ width: '100%' }}
                            disabled={
                                role === STATE_ROLE || role === QUERY_STATE_ROLE
                                    ? loading || !selectedEntity
                                    : false
                            }
                            options={
                                (role === STATE_ROLE || role === QUERY_STATE_ROLE)
                                    ? sectionOptions.map(id => ({ label: `Sección ${id}`, value: id }))
                                    : Array.from(new Set(route))
                                        .map(id => ({ label: `Sección ${id}`, value: id }))
                            }
                        />
                    </Col>
                </Row>


                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={stats.map((stat) => ({
                            key: `${stat.id}-${stat.sectionId}`,
                            ...stat,
                            progressPercentage: ((stat.totalCount || 0) / (stat.goal || 1)) * 100,
                        }))}
                        rowKey="key"
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalItems,
                            showSizeChanger: true,
                            pageSizeOptions: ["5", "10", "20"],
                        }}
                        onRow={(record) => {
                            return {
                                onClick: () => {
                                    setSelectedStat(record);
                                    setIsModalVisible(true);
                                },
                            };
                        }}
                        onShowSizeChange={(current, size) => {
                        }}
                        onChange={handleTableChange}
                    />
                </Spin>
            </motion.div>

            <Modal
                title="Detalles de la Estadística"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
            >
                {selectedStat && (
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="Entidad">{selectedStat.entityId}</Descriptions.Item>
                        <Descriptions.Item label="Sección">{selectedStat.sectionId}</Descriptions.Item>
                        <Descriptions.Item label="Meta mínima">{selectedStat.goal}</Descriptions.Item>
                        <Descriptions.Item label="Asistencia">{selectedStat.rollCallCount}</Descriptions.Item>
                        <Descriptions.Item label="Avance">{selectedStat.totalCount}</Descriptions.Item>
                        <Descriptions.Item
                            label="Avance (%)">{selectedStat.progressPercentage?.toFixed(2)}%</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </>
    )
        ;
};

export default StatisticsPage;